/**
 * GHL Custom Field Setup Page
 *
 * Creates custom fields in GHL organized by folders.
 * Checks for existing folders/fields to prevent duplicates.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTenant } from '../context/TenantContext';
import ghlService from '../services/ghlService';
import { fieldFolders, getTotalFieldCount, getTotalFolderCount } from '../utils/fieldConfig';

// Status types for log entries
const STATUS = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  SKIP: 'skip',
};

const SetupFields = () => {
  const { tenant } = useTenant();
  const primaryColor = tenant?.primaryColor || '#06AB8B';

  // Form state
  const [bearerToken, setBearerToken] = useState('');
  const [locationId, setLocationId] = useState('');

  // Process state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('');
  const [logs, setLogs] = useState([]);

  // Statistics
  const [stats, setStats] = useState({
    foldersCreated: 0,
    foldersSkipped: 0,
    fieldsCreated: 0,
    fieldsSkipped: 0,
    errors: 0,
  });

  /**
   * Add a log entry
   */
  const addLog = useCallback((message, status = STATUS.INFO) => {
    setLogs(prev => [...prev, { message, status, timestamp: new Date() }]);
  }, []);

  /**
   * Main function to create all custom fields
   */
  const handleCreateFields = async () => {
    // Validation
    if (!bearerToken.trim()) {
      addLog('Bearer token is required', STATUS.ERROR);
      return;
    }
    if (!locationId.trim()) {
      addLog('Location ID is required', STATUS.ERROR);
      return;
    }

    setIsProcessing(true);
    setIsCompleted(false);
    setLogs([]);
    setProgress(0);
    setStats({
      foldersCreated: 0,
      foldersSkipped: 0,
      fieldsCreated: 0,
      fieldsSkipped: 0,
      errors: 0,
    });

    try {
      // Set API credentials
      ghlService.setAuthToken(bearerToken.trim());
      ghlService.setLocationId(locationId.trim());

      addLog('Starting GHL custom field creation process...', STATUS.INFO);
      setCurrentStatus('Fetching existing folders and fields...');

      // Step 1: Fetch existing folders and fields
      let existingFields = [];
      try {
        existingFields = await ghlService.getCustomFields();
        addLog(`Found ${existingFields.length} existing custom fields/folders`, STATUS.INFO);
      } catch (err) {
        addLog(`Error fetching existing fields: ${err.message}`, STATUS.ERROR);
        setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
        // Continue anyway - will create all
      }

      // Extract folders from existing fields
      const existingFolders = existingFields.filter(f => f.dataType === 'FOLDER' || !f.parentId);

      const totalFolders = getTotalFolderCount();
      const totalFields = getTotalFieldCount();
      const totalOperations = totalFolders + totalFields;
      let completedOperations = 0;

      // Step 2: Process each folder and its fields
      for (const folderConfig of fieldFolders) {
        const { folder: folderName, fields } = folderConfig;

        setCurrentStatus(`Processing folder: ${folderName}`);

        // Check if folder exists
        let folderId = null;
        const existingFolder = ghlService.findFolderByName(existingFolders, folderName);

        if (existingFolder) {
          addLog(`Folder already exists: ${folderName}`, STATUS.SKIP);
          setStats(prev => ({ ...prev, foldersSkipped: prev.foldersSkipped + 1 }));
          folderId = existingFolder.id;
        } else {
          // Create folder
          try {
            addLog(`Creating folder: ${folderName}`, STATUS.INFO);
            const newFolder = await ghlService.createFolder(folderName);
            folderId = newFolder.customField?.id || newFolder.id;
            addLog(`Folder created: ${folderName}`, STATUS.SUCCESS);
            setStats(prev => ({ ...prev, foldersCreated: prev.foldersCreated + 1 }));

            // Add to existing folders for subsequent checks
            existingFolders.push({ id: folderId, name: folderName });
          } catch (err) {
            addLog(`Error creating folder "${folderName}": ${err.response?.data?.message || err.message}`, STATUS.ERROR);
            setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
            // Skip fields for this folder
            completedOperations += fields.length + 1;
            setProgress((completedOperations / totalOperations) * 100);
            continue;
          }
        }

        completedOperations++;
        setProgress((completedOperations / totalOperations) * 100);

        // Step 3: Create fields for this folder
        for (const fieldName of fields) {
          setCurrentStatus(`Creating field: ${fieldName}`);

          // Check if field exists
          const existingField = ghlService.findFieldByName(existingFields, fieldName, folderId);

          if (existingField) {
            addLog(`Field already exists: ${fieldName}`, STATUS.SKIP);
            setStats(prev => ({ ...prev, fieldsSkipped: prev.fieldsSkipped + 1 }));
          } else {
            // Create field
            try {
              await ghlService.createCustomField(fieldName, folderId);
              addLog(`Field created: ${fieldName}`, STATUS.SUCCESS);
              setStats(prev => ({ ...prev, fieldsCreated: prev.fieldsCreated + 1 }));

              // Add to existing fields for subsequent checks
              existingFields.push({ id: Date.now(), name: fieldName, parentId: folderId });
            } catch (err) {
              addLog(`Error creating field "${fieldName}": ${err.response?.data?.message || err.message}`, STATUS.ERROR);
              setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
            }
          }

          completedOperations++;
          setProgress((completedOperations / totalOperations) * 100);

          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      addLog('Process completed!', STATUS.SUCCESS);
      setCurrentStatus('Completed');
      setIsCompleted(true);

    } catch (err) {
      addLog(`Unexpected error: ${err.message}`, STATUS.ERROR);
      setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Get icon for log status
   */
  const getLogIcon = (status) => {
    switch (status) {
      case STATUS.SUCCESS:
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case STATUS.ERROR:
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case STATUS.SKIP:
        return <SkipNextIcon sx={{ color: 'warning.main' }} />;
      case STATUS.WARNING:
        return <ErrorIcon sx={{ color: 'warning.main' }} />;
      default:
        return <TextFieldsIcon sx={{ color: 'info.main' }} />;
    }
  };

  /**
   * Get color for log text
   */
  const getLogColor = (status) => {
    switch (status) {
      case STATUS.SUCCESS:
        return 'success.main';
      case STATUS.ERROR:
        return 'error.main';
      case STATUS.SKIP:
        return 'warning.main';
      case STATUS.WARNING:
        return 'warning.main';
      default:
        return 'text.primary';
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
        GHL Custom Field Setup
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create custom fields in Go High Level organized by folders. This tool checks for existing fields and folders to prevent duplicates.
      </Typography>

      {/* Configuration Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            API Configuration
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Bearer Token"
              value={bearerToken}
              onChange={(e) => setBearerToken(e.target.value)}
              placeholder="Enter your GHL API Bearer Token"
              fullWidth
              type="password"
              disabled={isProcessing}
            />

            <TextField
              label="Location ID"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="Enter your GHL Location ID"
              fullWidth
              disabled={isProcessing}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                icon={<FolderIcon />}
                label={`${getTotalFolderCount()} Folders`}
                variant="outlined"
              />
              <Chip
                icon={<TextFieldsIcon />}
                label={`${getTotalFieldCount()} Fields`}
                variant="outlined"
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleCreateFields}
              disabled={isProcessing || !bearerToken.trim() || !locationId.trim()}
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              sx={{
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: primaryColor, opacity: 0.9 },
                mt: 1,
              }}
            >
              {isProcessing ? 'Processing...' : 'Create Custom Fields'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Progress Card */}
      {(isProcessing || isCompleted) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Progress
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {currentStatus}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: primaryColor,
                  },
                }}
              />
            </Box>

            {/* Statistics */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<FolderIcon />}
                label={`Folders Created: ${stats.foldersCreated}`}
                color="success"
                size="small"
              />
              <Chip
                icon={<FolderIcon />}
                label={`Folders Skipped: ${stats.foldersSkipped}`}
                color="warning"
                size="small"
              />
              <Chip
                icon={<TextFieldsIcon />}
                label={`Fields Created: ${stats.fieldsCreated}`}
                color="success"
                size="small"
              />
              <Chip
                icon={<TextFieldsIcon />}
                label={`Fields Skipped: ${stats.fieldsSkipped}`}
                color="warning"
                size="small"
              />
              {stats.errors > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`Errors: ${stats.errors}`}
                  color="error"
                  size="small"
                />
              )}
            </Box>

            {isCompleted && (
              <Alert
                severity={stats.errors > 0 ? 'warning' : 'success'}
                sx={{ mt: 2 }}
              >
                {stats.errors > 0
                  ? `Process completed with ${stats.errors} error(s). Check logs for details.`
                  : 'All custom fields have been created successfully!'}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs Card */}
      {logs.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Activity Log
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                backgroundColor: '#f5f5f5',
              }}
            >
              <List dense>
                {logs.map((log, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getLogIcon(log.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={log.message}
                        secondary={log.timestamp.toLocaleTimeString()}
                        primaryTypographyProps={{
                          sx: { color: getLogColor(log.status) },
                        }}
                      />
                    </ListItem>
                    {index < logs.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Field Configuration Preview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Field Configuration Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following folders and fields will be created:
          </Typography>

          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {fieldFolders.map((folderConfig, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FolderIcon sx={{ color: primaryColor }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {folderConfig.folder}
                  </Typography>
                  <Chip
                    label={`${folderConfig.fields.length} fields`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ pl: 4 }}>
                  {folderConfig.fields.map((field, fieldIdx) => (
                    <Typography
                      key={fieldIdx}
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 0.25 }}
                    >
                      - {field}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SetupFields;
